/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Feature Flags Service - SuperAdmin Feature Flags API
 * ============================================================================
 */

import { get, post, put, del } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';

const BASE_URL = '/admin/feature-flags';

export interface FeatureFlag {
  id: string;
  flagCode: string;
  displayName: string;
  description: string | null;
  category: string | null;
  enabled: boolean;
  rolloutPercentage: number;
  overrideCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface FlagOverride {
  id: string;
  flagCode: string;
  orgId: string;
  enabled: boolean;
  reason: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface UpdateFlagRequest {
  enabled?: boolean;
  rolloutPercentage?: number;
  description?: string;
}

export interface CreateOverrideRequest {
  orgId: string;
  enabled: boolean;
  reason?: string;
}

export const featureFlagsService = {
  async getAllFlags(category?: string): Promise<FeatureFlag[]> {
    const params = category ? { category } : {};
    const response = await get<ApiResponse<FeatureFlag[]>>(BASE_URL, params);
    return response.data;
  },

  async getCategories(): Promise<string[]> {
    const response = await get<ApiResponse<string[]>>(`${BASE_URL}/categories`);
    return response.data;
  },

  async getFlag(code: string): Promise<FeatureFlag> {
    const response = await get<ApiResponse<FeatureFlag>>(`${BASE_URL}/${code}`);
    return response.data;
  },

  async updateFlag(code: string, request: UpdateFlagRequest): Promise<FeatureFlag> {
    const response = await put<ApiResponse<FeatureFlag>>(`${BASE_URL}/${code}`, request);
    return response.data;
  },

  async toggleFlag(code: string): Promise<FeatureFlag> {
    const response = await post<ApiResponse<FeatureFlag>>(`${BASE_URL}/${code}/toggle`, {});
    return response.data;
  },

  async getOverrides(code: string): Promise<FlagOverride[]> {
    const response = await get<ApiResponse<FlagOverride[]>>(`${BASE_URL}/${code}/overrides`);
    return response.data;
  },

  async createOverride(code: string, request: CreateOverrideRequest): Promise<FlagOverride> {
    const response = await post<ApiResponse<FlagOverride>>(`${BASE_URL}/${code}/overrides`, request);
    return response.data;
  },

  async deleteOverride(code: string, orgId: string): Promise<void> {
    await del<ApiResponse<void>>(`${BASE_URL}/${code}/overrides/${orgId}`);
  },
};

export default featureFlagsService;
